import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { ContainerGuests, DescriptionGuests, HeaderGuests, TitleGuests } from "../checkInForm/style";
import { Flex } from "@mantine/core";
import { HiIdentification } from "react-icons/hi";
import { Column } from "../../../../style";
import { secondary } from "../../../../theme";
import BiometricsStatus from "../../../BiometricsStatus";
import { DocumentOrPicture } from "../../ModalCheckIn/documentOrPicture";
import { Guest, IGroupProps } from "../../../../interfaces";
import { useGlobal } from "../../../../hooks/global";
import { UseFormClearErrors, UseFormSetError, UseFormSetValue } from "react-hook-form";
import { useApi } from "../../../../hooks/api";

interface IInitialImages {
    selfieImage: string | null,
    frontImage: string | null
}

interface IGuestsDocumentsCardProps {
    guestGroup: IGroupProps;
    _form: any;
    setValue: UseFormSetValue<any>;
    setError: UseFormSetError<any>;
    clearErrors: UseFormClearErrors<any>;
    ref: any
}

export const GuestsDocumentsCard = forwardRef((props: IGuestsDocumentsCardProps, ref) => {
    const { guestGroup, _form, setValue, setError, clearErrors } = props;
    const { theme } = useGlobal();
    const { api } = useApi();
    
    const [frontImage, setFrontImage] = useState<string | null>(null);
    const [selfieImage, setSelfieImage] = useState<string | null>(null);
    const [checkReleased, setCheckReleased] = useState(false);
    const [initialImages, setInitialImages] = useState<IInitialImages>({ selfieImage, frontImage });
    const [loadingMathImages, setLoadingMathImages] = useState(false);
    const [isValidMathImage, setIsValidMathImage] = useState(true);
    const [scoreMathImage, setScoreMathImage] = useState(0);
    const [isValidInputImages, setIsValidInputImages] = useState(false);
    const [isViewBiometricsStatus, setIsViewBiometricsStatus] = useState(false);
    const [hasEnabledFaceMatch, setHasEnabledFaceMatch] = useState(false);
    const [isChangePhotos, setIsChangePhotos] = useState(false);
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;
        
        const loadSettings = async () => {
            try {
                const { data } = await api.get("/constants");
                const setting = data?.ENABLED_FACE_MATCH_CHECK_IN_ADM;
                
                if (mounted) {
                    setHasEnabledFaceMatch(Boolean(setting));
                    setSettingsLoaded(true);
                }
            } catch (error) {
                console.error("Error fetching settings:", error);
                if (mounted) {
                    setHasEnabledFaceMatch(false);
                    setSettingsLoaded(true);
                }
            }
        };

        if (!settingsLoaded) {
            loadSettings();
        }
        
        return () => {
            mounted = false;
        };
    }, [api, settingsLoaded]);

    const matchImages = useCallback(async () => {
        if (!checkReleased) return { isVerified: true };
        
        if (!hasEnabledFaceMatch) {
            setIsViewBiometricsStatus(false);
            return { isVerified: true };
        }

        if (!selfieImage || !frontImage) {
            return { isVerified: false, error: "Missing images" };
        }

        setIsValidMathImage(true);
        setIsViewBiometricsStatus(true);
        setLoadingMathImages(true);
        
        try {
            const { data } = await api.post("/photos/compare", {
                photo_1: selfieImage,
                photo_2: frontImage,
                origin: 'ADM'
            });
            
            const isValid = Boolean(data?.valid);
            const score = Number(data?.confidence) || 0;
            
            setIsValidMathImage(isValid);
            setScoreMathImage(score);
            setValue(`compare-score-${guestGroup.uniqueId}`, score, { shouldValidate: true, shouldDirty: true });
            setValue(`compare-valid-${guestGroup.uniqueId}`, isValid, { shouldValidate: true, shouldDirty: true });
            
            return {
                isVerified: isValid,
                idGuest: guestGroup.uniqueId,
                compareValid: isValid,
                compareScore: score
            };
        } catch (error) {
            console.error("Error comparing images:", error);
            setIsValidMathImage(false);
            return { 
                isVerified: false, 
                error: "Failed to compare images",
                idGuest: guestGroup.uniqueId 
            };
        } finally {
            setLoadingMathImages(false);
        }
    }, [selfieImage, frontImage, checkReleased, hasEnabledFaceMatch, api, setValue, guestGroup.uniqueId]);

    useImperativeHandle(ref, () => ({
        matchImages: async () => {
            if (!selfieImage || !frontImage) {
                setError(`documents-required-guest-${guestGroup.uniqueId}`, {
                    type: "manual",
                    message: "Verificação da biometria não confirmada",
                });
                return { isVerified: false, error: "Missing required images" };
            }
            
            const result = await matchImages();
            return result;
        },
    }), [selfieImage, frontImage, matchImages, setError, guestGroup.uniqueId]);

    const handleDocumentFront = useCallback(async (guestId: string, newFile: string) => {
        setValue(`document-front-${guestId}`, newFile);
        setFrontImage(newFile);
    }, [setValue]);

    const handleDocumentSelfie = useCallback(async (guestId: string, newFile: string) => {
        setValue(`document-selfie-${guestId}`, newFile);
        setSelfieImage(newFile);
    }, [setValue]);

    useEffect(() => {
        if (initialImages.selfieImage === null && initialImages.frontImage === null) {
            setInitialImages({ selfieImage, frontImage });
        }
    }, [selfieImage, frontImage, initialImages.selfieImage, initialImages.frontImage]);

    useEffect(() => {
        if (!selfieImage || !frontImage) {
            setIsViewBiometricsStatus(false);
            setIsValidInputImages(false);
            setCheckReleased(false);
            return;
        }

        const imagesChanged =
            selfieImage !== initialImages.selfieImage ||
            frontImage !== initialImages.frontImage;
            
        if (imagesChanged) {
            setIsChangePhotos(true);
            setIsViewBiometricsStatus(false);
        }
        
        setCheckReleased(imagesChanged); 
        setIsValidInputImages(true);
        setIsValidMathImage(true);
        clearErrors(`documents-required-guest-${guestGroup.uniqueId}`);
    }, [selfieImage, frontImage, initialImages, clearErrors, guestGroup.uniqueId]);

    useEffect(() => {
        if (isChangePhotos || !_form?.guests) return;
        
        const guest = _form.guests.find((guest: Guest) => guest?.id === guestGroup.uniqueId);
        if (!guest) return;

        if (guest.compare_score && Number(guest.compare_score) > 0) {
            setIsViewBiometricsStatus(true);
            setScoreMathImage(Number(guest.compare_score));
        }
        
        if (guest.compare_valid) {
            setIsViewBiometricsStatus(true);
            setIsValidMathImage(Boolean(guest.compare_valid));
        }
    }, [_form?.guests, isChangePhotos, guestGroup.uniqueId]);

    useEffect(() => {
        if (!settingsLoaded) return;
        
        if (!selfieImage || !frontImage) {
            if (hasEnabledFaceMatch) {
                setError(`documents-required-guest-${guestGroup.uniqueId}`, {
                    type: "manual",
                    message: "Verificação da biometria não confirmada"
                });
            }
        } else {
            clearErrors(`documents-required-guest-${guestGroup.uniqueId}`);
        }
    }, [selfieImage, frontImage, hasEnabledFaceMatch, settingsLoaded, setError, clearErrors, guestGroup.uniqueId]);

    return (
        <ContainerGuests key={guestGroup.uniqueId} theme={theme} style={((isValidInputImages && isValidMathImage) || !hasEnabledFaceMatch) ? {} : { border: '1px solid red' }}>
            <HeaderGuests theme={theme} style={{ justifyContent: 'space-between' }}>
                <Flex gap={4} align='center'>
                    <HiIdentification size={24} color={secondary[theme]} />
                    <Column>
                        <TitleGuests>{`Documentos e selfie - [${_form[`name-${guestGroup.uniqueId}`]?.toUpperCase()?.trim()}]`}</TitleGuests>
                        <DescriptionGuests>Informe os documentos e selfie do hóspede</DescriptionGuests>
                    </Column>
                </Flex>

                {(isValidInputImages && isViewBiometricsStatus) && (
                    <BiometricsStatus
                        loadingMathImages={loadingMathImages}
                        valid={isValidMathImage}
                        score={scoreMathImage}
                    />
                )}

            </HeaderGuests>

            <div style={{ display: "flex", flexDirection: "row", flex: 1, gap: 20, padding: 20, flexWrap: 'wrap' }}>
                <DocumentOrPicture
                    onChange={(newFile) => handleDocumentFront(guestGroup?.uniqueId || '', newFile || '')}
                    defaultDocument={_form[`document-front-${guestGroup.uniqueId}`]}
                    name="Documento Frente"
                    type="document"
                    guest={_form[`name-${guestGroup.uniqueId}`]}
                />
                <DocumentOrPicture
                    onChange={(newFile) => setValue(`document-back-${guestGroup.uniqueId}`, newFile)}
                    defaultDocument={_form[`document-back-${guestGroup.uniqueId}`]}
                    name="Documento Verso"
                    type="document"
                    guest={_form[`name-${guestGroup.uniqueId}`]}
                />
                <DocumentOrPicture
                    onChange={(newFile) => handleDocumentSelfie(guestGroup?.uniqueId || '', newFile || '')}
                    defaultDocument={_form[`document-selfie-${guestGroup.uniqueId}`]}
                    name="Selfie"
                    type="selfie"
                    guest={_form[`name-${guestGroup.uniqueId}`]}
                />
            </div>

        </ContainerGuests>
    );
})
